import React from "react";
import { Sparkles, Target, TrendingUp, GraduationCap, Award, ListChecks, DollarSign, Briefcase, RefreshCw, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const READINESS_COLOR = { Low: "text-red-400 bg-red-400/10 border-red-400/30", Medium: "text-amber-400 bg-amber-400/10 border-amber-400/30", High: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" };

function PillList({ items, tone = "slate" }) {
  const toneClass = { slate: "bg-white/5 text-slate-300 border-white/10", emerald: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20", red: "bg-red-400/10 text-red-300 border-red-400/20", amber: "bg-indigo-400/10 text-indigo-300 border-indigo-400/20" }[tone];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, i) => (
        <span key={i} className={`text-xs px-3 py-1.5 rounded-full border ${toneClass}`}>{item}</span>
      ))}
    </div>
  );
}

export default function CareerReportTab({ report, onRegenerate, regenerating }) {
  if (!report) return null;
  return (
    <div className="space-y-6" data-testid="career-report-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Generated by AI from your platform activity
        </div>
        <button onClick={onRegenerate} disabled={regenerating} data-testid="career-regenerate-btn" className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg disabled:opacity-60">
          {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-6 text-center" data-testid="career-readiness-score">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Job Readiness Score</p>
          <p className="font-heading text-4xl font-bold text-indigo-300">{report.job_readiness_score}<span className="text-lg text-slate-400">/100</span></p>
          <Progress value={report.job_readiness_score} className="mt-3 h-2" />
        </div>
        <div className="glass-panel rounded-2xl p-6 text-center" data-testid="career-progress-pct">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Career Progress</p>
          <p className="font-heading text-4xl font-bold text-white">{report.career_progress_pct}%</p>
          <Progress value={report.career_progress_pct} className="mt-3 h-2" />
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-6" data-testid="career-summary">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-2"><GraduationCap className="w-3.5 h-3.5 text-indigo-400" /> AI Career Summary</p>
        <p className="text-sm text-slate-300 leading-relaxed">{report.ai_career_summary}</p>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border mt-4 ${READINESS_COLOR[report.estimated_interview_readiness] || READINESS_COLOR.Medium}`} data-testid="career-interview-readiness">
          Interview Readiness: {report.estimated_interview_readiness}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-2 mb-3"><TrendingUp className="w-3.5 h-3.5" /> Strength Areas</p>
          <PillList items={report.strength_areas} tone="emerald" />
        </div>
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 flex items-center gap-2 mb-3"><Target className="w-3.5 h-3.5" /> Weak Areas</p>
          <PillList items={report.weak_areas} tone="red" />
        </div>
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-2 mb-3"><ListChecks className="w-3.5 h-3.5" /> Recommended Topics</p>
          <PillList items={report.recommended_topics} tone="amber" />
        </div>
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#FBBF24] flex items-center gap-2 mb-3"><Award className="w-3.5 h-3.5" /> Recommended Certifications</p>
          <PillList items={report.recommended_certifications} tone="slate" />
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-6">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Next Learning Path</p>
        <ol className="space-y-2">
          {report.next_learning_path.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
              <span className="w-5 h-5 rounded-full bg-indigo-400/15 text-indigo-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
              {step}
            </li>
          ))}
        </ol>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-2"><Briefcase className="w-3.5 h-3.5" /> Market Demand</p>
          <p className="text-sm text-slate-300">{report.market_demand}</p>
        </div>
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-2"><DollarSign className="w-3.5 h-3.5" /> Salary Range</p>
          <p className="text-sm text-slate-300" data-testid="career-salary-range">{report.salary_range}</p>
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Recommended Practice Labs</p>
        <PillList items={report.recommended_practice_labs} tone="amber" />
      </div>

      <div className="glass-panel rounded-2xl p-6">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-4">Career Timeline</p>
        <div className="space-y-3">
          {report.career_timeline.map((t, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
              <span className="text-slate-300">{t.milestone}</span>
              <span className="text-indigo-400 font-semibold text-xs">{t.eta}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
